import * as React from 'react'
import Sidebar, { SidebarChildrenArray } from '@mulesoft/anypoint-components/lib/Sidebar'
import { Prompt, Redirect, Route, RouteComponentProps, Switch } from 'react-router'
import { hideIfPCEOrGovCloud } from '../../util/environments'
import { Profile, RoleTypes } from '../../lifecycle/auth'
import { isNullOrUndefined } from 'util'
import {
  flatRoutes,
  logManagementRouteTree,
  monitoringRouteTree,
  MonitoringRoute,
  otherRouteTree,
  routesWithoutSideBar
} from './Routes'
import SidebarHeading from '@mulesoft/anypoint-components/lib/SidebarHeading'
import SidebarGroup from '@mulesoft/anypoint-components/lib/SidebarGroup'
import SidebarLink from '@mulesoft/anypoint-components/lib/SidebarLink'
import SidebarDivider from '@mulesoft/anypoint-components/lib/SidebarDivider'
import visualizerApi from '../../util/VisualizerApi'
import './SideBar.css'
import { ValidatedRoute } from './ValidatedRoute'

export type Props = {
  profile: Profile
  defaultPathDisabled?: boolean
  defaultRouteTest?: string
  activePath?: string
  roles: RoleTypes[]
  hasEditRole?: boolean
}

export type State = {
  showMonitoring: boolean
  showLogManagement: boolean
  showOther: boolean
}

const FIRST_ROUTE = flatRoutes && flatRoutes[0]
const DEFAULT_PATH = (FIRST_ROUTE && FIRST_ROUTE.url) || '/'

export default class SideBar extends React.Component<Props, State> {

  static isSideBarHidden() {
    const hashPath = window.location.hash.split('#')[1]

    return routesWithoutSideBar.indexOf(hashPath) > -1
  }

  private static checkActive(url: string) {
    const actualUrl = window.location.hash.split('/')
    actualUrl.shift()
    const finalUrl = `/${actualUrl.join('/')}`

    return finalUrl.startsWith(url)
  }

  private nextUrl: string
  private onLinkClick = this.linkClick.bind(this)

  constructor(props: Props) {
    super(props)

    const {profile, roles} = props

    this.state = {
      showMonitoring: this.checkDivisionAppearance(monitoringRouteTree, profile, roles),
      showLogManagement: this.checkDivisionAppearance(logManagementRouteTree, profile, roles),
      showOther: this.checkDivisionAppearance(otherRouteTree, profile, roles)
    }

    this.hasUnsavedChanges = this.hasUnsavedChanges.bind(this)
    this.getPromptMessage = this.getPromptMessage.bind(this)
  }

  hasUnsavedChanges(activePath?: string, hasEditRole: boolean = false) {
    if (activePath) {
      return activePath.startsWith('/dashboard/') && hasEditRole && visualizerApi.hasChanges()
    } else {
      return window.location.hash.startsWith('#/dashboard/') && hasEditRole && visualizerApi.hasChanges()
    }
  }

  getPromptMessage() {
    const {activePath, hasEditRole} = this.props
    const hasDashboardUnsaved = !isNullOrUndefined(visualizerApi.getOriginalDashboard()) && this.hasUnsavedChanges(activePath, hasEditRole)

    return (hasDashboardUnsaved) ? 'Unsaved changes. Are you sure you want to leave?' : true
  }

  unsavedChangesModalHidden() {
    visualizerApi.removeListener('hide-modal', this.unsavedChangesModalHidden)
  }

  unsavedChangesModalDone() {
    visualizerApi.removeListener('unsaved-changes-done', this.unsavedChangesModalDone)
    window.location.hash = this.nextUrl
  }

  linkClick(event: Event, url: string) {
    const {activePath, hasEditRole} = this.props
    if (this.hasUnsavedChanges(activePath, hasEditRole)) {
      event.preventDefault()
      this.nextUrl = url
      visualizerApi.addListener('hide-modal', this.unsavedChangesModalHidden.bind(this))
      visualizerApi.addListener('unsaved-changes-done', this.unsavedChangesModalDone.bind(this))
      visualizerApi.showUnsavedChangedModal()
    }
  }

  navLinkClick(url: string, e: MouseEvent) {
    return this.onLinkClick(e, url)
  }

  renderSidebarGroup(routeTree: MonitoringRoute[], profile: Profile, roles: RoleTypes[]) {
    return (
      <SidebarGroup>
        {routeTree.filter(t => t.isVisible ?
          (t.isVisible(profile) && t.shouldDisplay && !hideIfPCEOrGovCloud(t.hideForPCE, t.hideForGovCloud)) &&
            this.hasRequiredRole(roles, t.roles) : false).map(r => {
              const url = r.urlResolver ? r.urlResolver(profile) : r.url;
              return <SidebarLink
                testId={url}
                key={url}
                href={`#${url}`}
                label={r.label}
                isActive={SideBar.checkActive(url)}
                onClick={this.navLinkClick.bind(this, url)}
              />;
            }

        )}
      </SidebarGroup>
    )
  }

  hasRequiredRole(userRoles: RoleTypes[], requiredRoles: RoleTypes[] | undefined): boolean {
    if (requiredRoles) {
      // intersect
      return userRoles.filter(role => requiredRoles.indexOf(role) > -1).length > 0
    } else {
      return true // No roles are required
    }
  }

  checkDivisionAppearance(routes: MonitoringRoute[], profile: Profile, roles: RoleTypes[]) {
    return routes.filter(t => t.isVisible ?
      (t.isVisible(profile) && t.shouldDisplay && !hideIfPCEOrGovCloud(t.hideForPCE, t.hideForGovCloud) && this.hasRequiredRole(roles, t.roles))
      : false).length > 0
  }

  routeComponent(validateRoute?: (url: { match: any }) => boolean,
                 component?: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>) {
    return (url: { match: any }) => {
      const props = {url, validateRoute, component}

      return <ValidatedRoute {...props} />
    }
  }

  componentDidMount() {
    const {activePath, hasEditRole} = this.props
    window.onbeforeunload = (event) => {
      if (!this.hasUnsavedChanges(activePath, hasEditRole)) return

      const e = event || window.event

      // For IE and Firefox prior to version 4
      const confirmationMessage = '\o/'
      if (e) {
        e.returnValue = confirmationMessage
      }
      // For Safari
      return confirmationMessage
    }
  }

  render() {
    const {profile, defaultPathDisabled, defaultRouteTest, roles} = this.props
    const {showMonitoring, showLogManagement, showOther} = this.state

    const sideBarItems: SidebarChildrenArray = []

    if (showMonitoring) {
      sideBarItems.push(<SidebarHeading icon="bar-chart-small" label="Monitoring"/>)
      sideBarItems.push(this.renderSidebarGroup(monitoringRouteTree, profile, roles))
    }

    if (showLogManagement) {
      sideBarItems.push(<SidebarDivider/>)
      sideBarItems.push(<SidebarHeading icon="terminal" label="Log Management"/>)
      sideBarItems.push(this.renderSidebarGroup(logManagementRouteTree, profile, roles))
    }

    if (showOther) {
      sideBarItems.push(<SidebarDivider/>)
      sideBarItems.push(<SidebarHeading icon="settings" label="Other"/>)
      sideBarItems.push(this.renderSidebarGroup(otherRouteTree, profile, roles))
    }

    return (
      <div className="side-bar">
        <div className={'side-bar-links'} hidden={SideBar.isSideBarHidden()}>
          <Sidebar className="anypoint-monitoring-sidebar" testId="anypoint-monitoring-sidebar">
            {sideBarItems}
          </Sidebar>
        </div>

        <div className={`side-bar-content ${SideBar.isSideBarHidden() && 'side-bar-links-hidden'}`}>
          <Switch>
            {flatRoutes.filter(
                t => t.isVisible ? (t.isVisible(profile) && !hideIfPCEOrGovCloud(t.hideForPCE)) && this.hasRequiredRole(roles, t.roles) : false
            ).map(r => (
              <Route path={r.url} exact={true} component={this.routeComponent(r.validateRoute, r.component)} key={r.url}/>
            ))}
            {!defaultPathDisabled && DEFAULT_PATH && <Redirect to={defaultRouteTest || DEFAULT_PATH}/>}
          </Switch>
        </div>

        <Prompt message={this.getPromptMessage}/>
      </div>
    )
  }
}
